import { FaCloudUploadAlt, FaTrashAlt } from "react-icons/fa";
import React, { useEffect, useState } from "react";
import { fileUpload, postRequest } from "src/service/api-services/api-services";

import { API_URL } from "src/service/config";
import showToast from "@utils/toast";

const TrashIcon = FaTrashAlt as React.FC;
const CloudUploadIcon = FaCloudUploadAlt as React.FC;

export interface IGraphicApiResponse {
  result: {
    doNotExit: assetToVerify[];
    exists: assetToVerify[];
  };
}

type assetToVerify = {
  code: string;
};

const AssetsUploadForm = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [unverifiedFiles, setUnverifiedFiles] = useState<assetToVerify[] | []>(
    [],
  );
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [assetTypeUpload, setAssetTypeUpload] = useState<
    "videos" | "audios" | "graphics"
  >("graphics");
  const [isSubmitting, setSubmit] = useState<boolean>(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files ? Array.from(e.target.files) : [];
    setFiles((prev) => [...prev, ...selected]);
  };

  const removeFileExt = (name: string) => {
    return name.split(".")[0];
  };

  const removeFile = (name: string) => {
    console.log(name);
    setFiles((prev) => prev.filter((file) => file.name !== name));
    setUnverifiedFiles((prev) =>
      prev.filter((file) => file.code !== removeFileExt(name)),
    );
  };

  const fileWithExt = (fileName: string) => {
    const filteFileWithExt = files.find(
      (file) => removeFileExt(file.name) === fileName,
    );
    return filteFileWithExt;
  };

  const verifyFiles = async () => {
    const codeListToVerify: assetToVerify[] = [];
    files.map((file) =>
      codeListToVerify.push({ code: removeFileExt(file.name) }),
    );

    try {
      const response = await postRequest<IGraphicApiResponse>(
        `${API_URL}/${assetTypeUpload}/check-declared`,
        codeListToVerify,
      );

      setUnverifiedFiles(response.result.doNotExit);
      setIsVerified(response.result.doNotExit.length === 0);
    } catch (error) {
      showToast("Verification failed. Try again.", false);
    }
  };

  const handleSubmit = async () => {
    setSubmit(true);
    const allowedTypes = ["image/png", "audio/mpeg", "video/mp4"]; //mp3 mintyp=mp3 m4a size = 1mb- 2mb // max -min
    const invalidFiles = files.filter(
      (file) => !allowedTypes.includes(file.type),
    );

    if (invalidFiles.length > 0) {
      const names = invalidFiles.map((f) => f.name).join(", ");
      showToast(`Invalid file types: ${names}`, false);
      return;
    }

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    try {
      await fileUpload(`${API_URL}/${assetTypeUpload}/upload`, formData);
      showToast("Files uploaded successfully!", true);
      setFiles([]);
      setUnverifiedFiles([]);
      setIsVerified(false);
      setSubmit(false);
    } catch (error) {
      setSubmit(false);
      const message =
        error instanceof Error ? error.message : "Upload failed. Try again.";
      showToast(message, false);
    }
  };

  useEffect(() => {
    if (files.length > 0) verifyFiles();
  }, [files, assetTypeUpload]);

  const renderFileCard = (
    file: File | string | undefined,
    isUnverified = false,
  ) => {
    const fileObj =
      typeof file === "string" ? files.find((f) => f.name === file) : file;
    if (!fileObj) return null;

    const fileURL = URL.createObjectURL(fileObj);
    const isImage = fileObj.type.startsWith("image/");
    const isAudio = fileObj.type.startsWith("audio/");
    const isVideo = fileObj.type.startsWith("video/");

    return (
      <div
        key={fileObj.name}
        className={`bg-white border ${
          isUnverified ? "border-yellow-400" : "border-gray-300"
        } rounded-md p-3 shadow-sm flex flex-col items-center justify-between transition-transform duration-300 hover:scale-105`}
      >
        <div className="w-full h-32 flex items-center justify-center mb-2">
          {isImage ? (
            <img
              src={fileURL}
              alt={fileObj.name}
              className="object-cover h-full w-full rounded-md"
            />
          ) : isAudio ? (
            <audio controls className="w-full">
              <source src={fileURL} type={fileObj.type} />
              Your browser does not support the audio element.
            </audio>
          ) : isVideo ? (
            <video controls className="w-full h-full rounded-md">
              <source src={fileURL} type={fileObj.type} />
              Your browser does not support the video tag.
            </video>
          ) : (
            <div className="text-gray-500 text-sm">📄{fileObj.type}</div>
          )}
        </div>

        <div className="flex justify-between items-center w-full">
          <span
            className={`text-xs truncate ${
              isUnverified ? "text-yellow-700" : "text-gray-700"
            }`}
          >
            {fileObj.name}
          </span>
          <button
            onClick={() => removeFile(fileObj.name)}
            className="text-red-500 hover:text-red-700 transition-colors"
          >
            <TrashIcon />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 relative">
      <div className="bg-white shadow-md px-6 py-4 flex justify-between items-center sticky top-0 z-10">
        <h1 className="text-xl font-bold text-gray-800">
          Upload Asset Content
        </h1>

        <select
          id="language-select"
          className="border border-gray-300 rounded-md px-3 py-2"
          value={assetTypeUpload.toString()}
          onChange={(e) => setAssetTypeUpload(e.target.value as any)}
        >
          <option value="">Select Asset Type</option>
          <option value={"audios"}>Audios</option>
          <option value={"videos"}>Videos</option>
          <option value={"graphics"}>Graphics</option>
        </select>

        <label className="flex items-center gap-2 cursor-pointer bg-brand text-white px-4 py-2 rounded hover:bg-brand transition">
          <CloudUploadIcon />
          <span>
            {assetTypeUpload === "audios"
              ? "Select Audio Files"
              : assetTypeUpload === "graphics"
                ? "Select Graphic Files"
                : "Select Video Files"}
          </span>
          <input
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
        </label>
      </div>

      {files.length > 0 && (
        <div className="px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-700 mb-2">
            Uploaded Files
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {files.map((file) => renderFileCard(file))}
          </div>
        </div>
      )}

      {unverifiedFiles.length > 0 && (
        <div className="px-6 py-4">
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-4 rounded-md mb-4">
            ⚠️ Some files are not verified. Please review and remove them.
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {unverifiedFiles.map((file) =>
              renderFileCard(fileWithExt(file.code), true),
            )}
          </div>
        </div>
      )}

      {files.length > 0 && (
        <div className="fixed bottom-6 right-6">
          <button
            onClick={handleSubmit}
            disabled={!isVerified || isSubmitting}
            className={`px-6 py-3 rounded-full shadow-lg text-white font-semibold transition ${
              isVerified
                ? "bg-green-600 hover:bg-green-700"
                : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            {isSubmitting ? "Uploading Files...." : "Submit Files"}
          </button>
        </div>
      )}
    </div>
  );
};

export default AssetsUploadForm;
